import * as Sentry from '@sentry/react';
import stringify from 'json-stringify-safe';

export const MAX_DATA_LENGTH = 1024 * 200;
export const FIELD_IS_EMPTY = 'FIELD_IS_EMPTY';
export const EXCLUDED_STORE_KEYS = [
    'trl',
    'areaTree',
    'profareaTree',
    'industriesTree',
    'professionalRoleTree',
    'contactPhones',
    'xsrfToken',
    'languages',
];

type Enhancer = () => void;

const createSentryReduxEnhancer = ({
    excludedKeys = [],
    maxDataLength,
    actionTransformer,
    stateTransformer,
    attachReduxState = true,
}: {
    excludedKeys?: string[];
    maxDataLength?: number;
    actionTransformer: (action: { type: string }) => { type: string } | null;
    stateTransformer: (state: Record<string, unknown>) => Record<string, unknown> | null;
    attachReduxState?: boolean;
}): Enhancer => {
    return Sentry.createReduxEnhancer({
        attachReduxState,
        actionTransformer: (action: { type: string }) => {
            if (action.type.includes('ANALYTIC')) {
                return null;
            }
            if (actionTransformer && actionTransformer(action) === null) {
                return null;
            }
            return action;
        },
        stateTransformer: (state: Record<string, unknown>) => {
            let transformedState = { ...state };
            const originalTrl = transformedState?.trl;

            EXCLUDED_STORE_KEYS.concat(excludedKeys).forEach((key) => {
                transformedState[key] = '[removed]';
            });
            const vacancyFull = transformedState?.vacancyFull as Record<string, string> | undefined;
            if (vacancyFull?.description) {
                vacancyFull.description = '[removed]';
            }
            if (stateTransformer) {
                const newState = stateTransformer(transformedState);
                if (newState === null) {
                    return null;
                }
                transformedState = newState;
            }
            const stateLength = stringify(transformedState).length;
            const maxStateLength = maxDataLength ?? MAX_DATA_LENGTH;
            if (stateLength > maxStateLength) {
                return {
                    trlsLength: originalTrl ? stringify(originalTrl).length : 0,
                    basicState: {
                        request: transformedState?.request ?? FIELD_IS_EMPTY,
                        locale: transformedState?.locale ?? FIELD_IS_EMPTY,
                        langs: transformedState?.langs ?? FIELD_IS_EMPTY,
                    },
                    __removed: `${'State is out of limit and has been stripped ('}${stateLength}/${maxStateLength})`,
                };
            }
            return transformedState;
        },
    }) as Enhancer;
};

export default createSentryReduxEnhancer;
